// §34.3 shop bays + availability-first job scheduling - request/response
// schemas for the new bays CRUD, day/range availability, and job scheduling
// endpoints (server/src/modules/scheduling). Pure zod, no server imports -
// same split as every other schemas/*.ts file.
import { z } from 'zod';
import { nonEmptyTrimmed, uuidSchema } from './common.js';
const MAX_AVAILABILITY_RANGE_DAYS = 62;
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be an ISO date (YYYY-MM-DD)');
// ---------------------------------------------------------------------------
// bays CRUD
// ---------------------------------------------------------------------------
export const baySchema = z.object({
    id: uuidSchema,
    name: z.string(),
    sort: z.number().int(),
    isActive: z.boolean(),
    locationId: uuidSchema,
});
export const createBaySchema = z.object({
    name: nonEmptyTrimmed(60),
    sort: z.number().int().optional(),
    locationId: uuidSchema.optional(),
});
export const updateBaySchema = z.object({
    name: nonEmptyTrimmed(60).optional(),
    sort: z.number().int().optional(),
    isActive: z.boolean().optional(),
    locationId: uuidSchema.optional(),
});
// ---------------------------------------------------------------------------
// availability (range) + day detail
// ---------------------------------------------------------------------------
export const availabilityQuerySchema = z
    .object({ from: isoDateSchema, to: isoDateSchema, locationId: uuidSchema.optional() })
    .check((ctx) => {
    const from = Date.parse(ctx.value.from);
    const to = Date.parse(ctx.value.to);
    if (to < from) {
        ctx.issues.push({ code: 'custom', message: '"to" must not be before "from"', input: ctx.value, path: ['to'] });
        return;
    }
    const days = Math.round((to - from) / 86_400_000) + 1;
    if (days > MAX_AVAILABILITY_RANGE_DAYS) {
        ctx.issues.push({
            code: 'custom',
            message: `range cannot exceed ${MAX_AVAILABILITY_RANGE_DAYS} days`,
            input: ctx.value,
            path: ['to'],
        });
    }
});
export const dayAvailabilitySchema = z.object({
    date: isoDateSchema,
    state: z.enum(['available', 'tight', 'full', 'closed']),
    baysFreeAtPeak: z.number().int(),
    bayCount: z.number().int(),
    installerMinutesFree: z.number().int(),
    installerMinutesTotal: z.number().int(),
});
export const dayDetailQuerySchema = z.object({
    date: isoDateSchema,
    jobId: uuidSchema.optional(),
    locationId: uuidSchema.optional(),
});
export const daySegmentSchema = z.object({
    id: uuidSchema,
    bayId: uuidSchema,
    jobId: uuidSchema,
    kind: z.enum(['work', 'cure_hold']),
    startsAt: z.string(),
    endsAt: z.string(),
    jobNumber: z.number().nullable(),
    customerName: z.string().nullable(),
});
export const daySlotsSchema = z.object({
    bayId: uuidSchema,
    slots: z.array(z.string()),
});
export const dayDetailSchema = z.object({
    date: isoDateSchema,
    window: z.object({ startsAt: z.string(), endsAt: z.string() }).nullable(),
    bays: z.array(baySchema),
    segments: z.array(daySegmentSchema),
    job: z
        .object({
        id: uuidSchema,
        estimatedMinutes: z.number().int().nullable(),
        cureHoldMinutes: z.number().int().nullable(),
        preferredDate: isoDateSchema.nullable(),
        preferredTime: z.number().int().nullable(),
    })
        .nullable(),
    openSlots: z.array(daySlotsSchema).nullable(),
});
// ---------------------------------------------------------------------------
// job scheduling
// ---------------------------------------------------------------------------
export const scheduleJobSchema = z
    .object({
    startsAt: z.string().min(1),
    durationMinutes: z.number().int().min(1).optional(),
    bayId: uuidSchema.optional(),
    dayBays: z.array(z.object({ date: isoDateSchema, bayId: uuidSchema })).optional(),
})
    .check((ctx) => {
    const hasBay = ctx.value.bayId != null;
    const hasDayBays = ctx.value.dayBays != null;
    if (hasBay === hasDayBays) {
        ctx.issues.push({ code: 'custom', message: 'exactly one of bayId or dayBays is required', input: ctx.value, path: ['bayId'] });
        return;
    }
    if (hasDayBays && ctx.value.dayBays.length === 0) {
        ctx.issues.push({ code: 'custom', message: 'dayBays must not be empty', input: ctx.value, path: ['dayBays'] });
    }
});
export const scheduleJobResultSchema = z.object({
    startsAt: z.string(),
    endsAt: z.string(),
    cureEndsAt: z.string().nullable(),
    segments: z.array(z.object({ bayId: uuidSchema, startsAt: z.string(), endsAt: z.string() })),
});
//# sourceMappingURL=scheduling.js.map